import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, Users, Send, Tag, Loader } from 'lucide-react';

interface ScheduleCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduledAt: Date, filterTags: string[]) => void;
  subscriberCount: number;
  campaignSubject: string;
  availableTags?: string[];
  onCountForTags?: (tags: string[]) => Promise<number>;
}

export function ScheduleCampaignModal({
  isOpen,
  onClose,
  onConfirm,
  subscriberCount,
  campaignSubject,
  availableTags = [],
  onCountForTags,
}: ScheduleCampaignModalProps) {
  const [scheduleOption, setScheduleOption] = useState<'now' | 'later'>('now');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [error, setError] = useState('');
  const [audience, setAudience] = useState<'all' | 'groups'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagCount, setTagCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const minDate = new Date(now.getTime() + 6 * 60000);

      setSelectedDate(minDate.toISOString().split('T')[0]);
      setSelectedTime(minDate.toTimeString().slice(0, 5));
      setError('');
      setAudience('all');
      setSelectedTags([]);
      setTagCount(null);
    }
  }, [isOpen]);

  // Live teller voor de gekozen groepen.
  useEffect(() => {
    if (!isOpen || audience !== 'groups' || selectedTags.length === 0 || !onCountForTags) {
      setTagCount(null);
      return;
    }

    let cancelled = false;
    setCountLoading(true);
    onCountForTags(selectedTags)
      .then((count) => {
        if (!cancelled) setTagCount(count);
      })
      .catch(() => {
        if (!cancelled) setTagCount(null);
      })
      .finally(() => {
        if (!cancelled) setCountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, audience, selectedTags, onCountForTags]);

  if (!isOpen) return null;

  const hasGroups = availableTags.length > 0;
  const usingGroups = audience === 'groups';
  const effectiveCount = usingGroups ? tagCount : subscriberCount;
  const confirmDisabled =
    usingGroups && (selectedTags.length === 0 || countLoading || tagCount === 0);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const getScheduledDateTime = (): Date | null => {
    if (scheduleOption === 'now') {
      return new Date(Date.now() + 5 * 60000);
    }

    if (!selectedDate || !selectedTime) {
      setError('Selecteer een datum en tijd');
      return null;
    }

    const scheduled = new Date(`${selectedDate}T${selectedTime}`);
    const now = new Date();
    const minTime = new Date(now.getTime() + 5 * 60000);

    if (scheduled < minTime) {
      setError('Geplande tijd moet minimaal 5 minuten in de toekomst zijn');
      return null;
    }

    return scheduled;
  };

  const handleConfirm = () => {
    const scheduledAt = getScheduledDateTime();
    if (scheduledAt) {
      onConfirm(scheduledAt, usingGroups ? selectedTags : []);
      onClose();
    }
  };

  const formatScheduledTime = () => {
    const dt = getScheduledDateTime();
    if (!dt) return '';

    return dt.toLocaleString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#231F20]">Campagne inplannen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {campaignSubject}
                </p>
                <p className="text-sm text-blue-800">
                  Je staat op het punt deze campaign te versturen naar{' '}
                  {usingGroups && selectedTags.length === 0 ? (
                    <span className="font-bold">de contacten in de groep(en) die je hieronder kiest</span>
                  ) : countLoading ? (
                    <span className="font-bold inline-flex items-center gap-1">
                      <Loader className="w-3.5 h-3.5 animate-spin" /> contacten tellen...
                    </span>
                  ) : (
                    <span className="font-bold">
                      {effectiveCount ?? 0} geabonneerde contact{(effectiveCount ?? 0) !== 1 ? 'en' : ''}
                      {usingGroups ? ` in ${selectedTags.length === 1 ? `de groep "${selectedTags[0]}"` : 'de gekozen groepen'}` : ''}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {hasGroups && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Naar wie wil je versturen?
              </label>

              <button
                onClick={() => setAudience('all')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  audience === 'all'
                    ? 'border-[#FF6B35] bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  audience === 'all' ? 'border-[#FF6B35]' : 'border-gray-300'
                }`}>
                  {audience === 'all' && (
                    <div className="w-3 h-3 rounded-full bg-[#FF6B35]" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-gray-700" />
                    <span className="font-semibold text-gray-900">Alle contacten</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {subscriberCount} geabonneerde contact{subscriberCount !== 1 ? 'en' : ''}
                  </p>
                </div>
              </button>

              <button
                onClick={() => setAudience('groups')}
                className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                  audience === 'groups'
                    ? 'border-[#FF6B35] bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  audience === 'groups' ? 'border-[#FF6B35]' : 'border-gray-300'
                }`}>
                  {audience === 'groups' && (
                    <div className="w-3 h-3 rounded-full bg-[#FF6B35]" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag className="w-4 h-4 text-gray-700" />
                    <span className="font-semibold text-gray-900">Specifieke groepen</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Alleen contacten in de groepen die je kiest
                  </p>
                </div>
              </button>

              {audience === 'groups' && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Kies één of meer groepen:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map((tag) => {
                      const selected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => toggleTag(tag)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            selected
                              ? 'bg-[#FF6B35] border-[#FF6B35] text-white'
                              : 'bg-white border-gray-300 text-gray-700 hover:border-[#FF6B35]'
                          }`}
                        >
                          <Tag className="w-3.5 h-3.5" />
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                  {selectedTags.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Selecteer minimaal één groep om verder te gaan.
                    </p>
                  )}
                  {selectedTags.length > 0 && tagCount === 0 && !countLoading && (
                    <p className="text-xs text-red-600 mt-2">
                      Geen geabonneerde contacten in deze selectie — kies een andere groep.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Wanneer wil je versturen?
            </label>

            <button
              onClick={() => setScheduleOption('now')}
              className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                scheduleOption === 'now'
                  ? 'border-[#FF6B35] bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                scheduleOption === 'now' ? 'border-[#FF6B35]' : 'border-gray-300'
              }`}>
                {scheduleOption === 'now' && (
                  <div className="w-3 h-3 rounded-full bg-[#FF6B35]" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-4 h-4 text-gray-700" />
                  <span className="font-semibold text-gray-900">Nu versturen</span>
                </div>
                <p className="text-sm text-gray-600">
                  Campagne wordt ingepland voor over 5 minuten
                </p>
              </div>
            </button>

            <button
              onClick={() => setScheduleOption('later')}
              className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                scheduleOption === 'later'
                  ? 'border-[#FF6B35] bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                scheduleOption === 'later' ? 'border-[#FF6B35]' : 'border-gray-300'
              }`}>
                {scheduleOption === 'later' && (
                  <div className="w-3 h-3 rounded-full bg-[#FF6B35]" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-gray-700" />
                  <span className="font-semibold text-gray-900">Inplannen voor later</span>
                </div>
                <p className="text-sm text-gray-600">
                  Kies een specifieke datum en tijd
                </p>
              </div>
            </button>
          </div>

          {scheduleOption === 'later' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Datum
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setError('');
                  }}
                  className="input w-full"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Tijd
                </label>
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => {
                    setSelectedTime(e.target.value);
                    setError('');
                  }}
                  className="input w-full"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!error && (scheduleOption === 'now' || (selectedDate && selectedTime)) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-900">
                <span className="font-semibold">Ingepland voor:</span>
                <br />
                {formatScheduledTime()}
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">
            Annuleren
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={`btn-primary flex-1 ${confirmDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Bevestig en plan in
          </button>
        </div>
      </div>
    </div>
  );
}
